import { Injectable, OnDestroy } from "@angular/core";
import { OAuthService } from "angular-oauth2-oidc";

interface RefreshLeaderLock {
	owner: string;
	expiresAt: number;
}

@Injectable({ providedIn: "root" })
export class OauthRefreshLeaderService implements OnDestroy {
	private readonly lockKey = "oauth_refresh_leader_lock";
	private readonly leaseMs = 15000;
	private readonly heartbeatMs = 5000;
	private readonly electionMs = 4000;
	private readonly tabId = this.createTabId();

	private started = false;
	private leader = false;
	private heartbeatHandle?: number;
	private electionHandle?: number;

	constructor(private oauthService: OAuthService) {}

	start(): void {
		if (this.started) {
			return;
		}

		this.started = true;
		this.maintainLeadership();
		this.electionHandle = window.setInterval(() => this.maintainLeadership(), this.electionMs);
		window.addEventListener("storage", this.onStorageEvent);
		window.addEventListener("beforeunload", this.onBeforeUnload);
	}

	ngOnDestroy(): void {
		this.stop();
	}

	private stop(): void {
		if (!this.started) {
			return;
		}

		this.started = false;
		this.releaseIfLeader();
		this.stepDown();
		if (this.electionHandle) {
			window.clearInterval(this.electionHandle);
			this.electionHandle = undefined;
		}
		window.removeEventListener("storage", this.onStorageEvent);
		window.removeEventListener("beforeunload", this.onBeforeUnload);
	}

	private onBeforeUnload = () => {
		this.releaseIfLeader();
	};

	private onStorageEvent = (event: StorageEvent) => {
		if (event.key !== this.lockKey) {
			return;
		}
		this.maintainLeadership();
	};

	private maintainLeadership(): void {
		if (!this.oauthService.hasValidAccessToken()) {
			this.releaseIfLeader();
			this.stepDown();
			return;
		}

		const now = Date.now();
		const lock = this.readLock();

		if (this.leader) {
			if (lock?.owner && lock.owner !== this.tabId && lock.expiresAt > now) {
				this.stepDown();
				return;
			}
			this.writeLock(now);
			return;
		}

		if (!lock || lock.expiresAt <= now || lock.owner === this.tabId) {
			this.tryBecomeLeader(now);
		}
	}

	private tryBecomeLeader(_now: number): void {
		const jitter = Math.floor(Math.random() * 200);
		setTimeout(() => {
			if (!this.started) {
				return;
			}
			this.writeLock(Date.now());
			const confirmedLock = this.readLock();
			if (confirmedLock?.owner === this.tabId) {
				this.becomeLeader();
			}
		}, jitter);
	}

	private becomeLeader(): void {
		if (this.leader) {
			return;
		}

		this.leader = true;
		this.oauthService.setupAutomaticSilentRefresh();
		this.heartbeatHandle = window.setInterval(() => this.writeLock(Date.now()), this.heartbeatMs);
	}

	private stepDown(): void {
		if (!this.leader) {
			return;
		}

		this.leader = false;
		if (this.heartbeatHandle) {
			window.clearInterval(this.heartbeatHandle);
			this.heartbeatHandle = undefined;
		}
		this.oauthService.stopAutomaticRefresh();
	}

	private releaseIfLeader(): void {
		if (!this.leader) {
			return;
		}

		const lock = this.readLock();
		if (lock?.owner === this.tabId) {
			localStorage.removeItem(this.lockKey);
		}
	}

	private writeLock(now: number): void {
		const lock: RefreshLeaderLock = {
			owner: this.tabId,
			expiresAt: now + this.leaseMs,
		};
		localStorage.setItem(this.lockKey, JSON.stringify(lock));
	}

	private readLock(): RefreshLeaderLock | null {
		const raw = localStorage.getItem(this.lockKey);
		if (!raw) {
			return null;
		}

		try {
			const parsed = JSON.parse(raw) as RefreshLeaderLock;
			if (!parsed?.owner || typeof parsed.expiresAt !== "number") {
				return null;
			}
			return parsed;
		} catch {
			return null;
		}
	}

	private createTabId(): string {
		if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
			return crypto.randomUUID();
		}
		return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
	}
}
